import { Kind } from "@app/data";
import { KeyValuePair } from "@app/types/Page";
import { SMX_PREBID_JS } from "@components/Advertisement/utils/constants";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";

// import { IS_PLAYWRIGHT } from "@util/constant";
import { getAdUnitData } from "./utils/helpers";
import { AdUnitNames } from "./utils/types";

export type AdSettingsProps = {
  adNames: AdUnitNames[];
  path: string;
  adTargets?: KeyValuePair<string, string>[]; // page level ad targets
  slotTargets?: KeyValuePair<string, string>[]; // slot level ad targets
  pageType?: Kind;
};

export default function AdSettings({
  adNames,
  path,
  adTargets = [], // page level ad targets
  slotTargets = [], // slot level ad targets
  pageType,
}: AdSettingsProps): React.ReactElement {
  const { userInfo } = useUserInfo();

  const encodedPath =
    typeof path !== undefined ? path.replaceAll("'", "%27") : "";

  // Disable ads in playwright. @TODO: Add no ad setting css after initial all migrated
  // if (IS_PLAYWRIGHT) {
  //   return <NoAdSettings />;
  // }

  // If initial gsChannels is empty string, do not render the ad settings.
  const gsChannels = slotTargets.find(
    (target) => target.key === "gs_channels"
  )?.value;

  if (gsChannels === "") return <></>;

  const adUnits = getAdUnitData(adNames, encodedPath, slotTargets);
  const pageData = {
    pageType: pageType,
    path: encodedPath,
    adUnits: adUnits,
    adTargets: adTargets,
    userInfo,
  };
  const loaded_dfp_tags = adUnits.reduce((previous, current) => {
    return {
      ...previous,
      [current.type]: {
        adunit: current.pattern,
        slotname: current.id,
        size: Object.keys(current.slotSize)
          .map((key) => {
            const sizes = current.slotSize?.[parseInt(key)];

            return sizes?.toString().replaceAll(",", "x");
          })
          .join(","),
      },
    };
  }, {});

  return (
    <>
      <script
        id="ads-settings"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{
          __html: `${[
            "window.bt_page_data = " + JSON.stringify(pageData) + ";",
            "var loaded_dfp_tags = '" + JSON.stringify(loaded_dfp_tags) + "';",
          ].join(" ")}(${overlaySEF.toString()})()`,
        }}
      />

      <script
        id="smx-prebid-js"
        type="text/javascript"
        src={SMX_PREBID_JS}
      ></script>

      <script
        type="text/javascript"
        src="https://cdn.sphlabs.com/suid/suid.min.js"
      />

      {/* Moved to useAllCustomHooks() */}
      {/* <script
        type="text/javascript"
        src="https://adtag.sphdigital.com/tag/smx/prebid.js"
      /> */}

      <script
        type="text/javascript"
        src="/_web2/3.0.4/bt_ads.js?v=3.0.4"
        defer
      ></script>

      <script
        type="text/javascript"
        src="/_web2/betterads_head2.js?v=3.0.4"
        defer
      />
    </>
  );
}

// a self executing function injected into the head
const overlaySEF = function () {
  if (typeof window === "undefined") return;

  const hasDfpPreview = window.location.search.includes("dfp_preview");

  if (screen.width >= 768 && hasDfpPreview) {
    setTimeout(function () {
      const myPageOverlay = document.getElementById("MyPageOverlay");
      if (myPageOverlay) myPageOverlay?.classList.remove("overlayWhite");
    }, 4e3);
  }
};
